/**
 * AgendaCalendarDialog — monthly calendar view of the Cronograma.
 *
 * Shown as a dialog from the Dashboard widget (and inline on the
 * /cronograma page).  Mimics the native-phone calendar card the user
 * requested:
 *   • Month name header with prev/next arrows
 *   • D S T Q Q S S row
 *   • Day cells with today pill + a colored dot for days that have
 *     scheduled items
 *   • Tapping a day highlights it and reveals the item list below
 *   • Tapping an item opens the full AgendaItemFormDialog in edit mode
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft, ChevronRight, Plus, Clock, CheckCircle2, RefreshCcw, Flag,
  CalendarDays,
} from "lucide-react";
import AgendaItemFormDialog from "@/components/AgendaItemFormDialog";

const MONTH_NAMES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];
const DAY_SHORT = ["D", "S", "T", "Q", "Q", "S", "S"];

const STATUS_META = {
  afio:       { label: "A fazer",    tint: "bg-slate-100 text-slate-700",     dot: "bg-slate-400",   Icon: Clock },
  feito:      { label: "Feito",      tint: "bg-emerald-100 text-emerald-800", dot: "bg-emerald-500", Icon: CheckCircle2 },
  reagendado: { label: "Reagendado", tint: "bg-amber-100 text-amber-800",     dot: "bg-amber-500",   Icon: RefreshCcw },
  finalizado: { label: "Finalizado", tint: "bg-indigo-100 text-indigo-800",   dot: "bg-indigo-500",  Icon: Flag },
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const pad2 = (n) => String(n).padStart(2, "0");

/** Build a 6-row x 7-col grid of ISO date strings for the month view. */
function buildMonthGrid(year, month /* 1-12 */) {
  const first = new Date(year, month - 1, 1);
  const startDow = first.getDay(); // 0 = Sunday
  const start = new Date(year, month - 1, 1 - startDow);
  const grid = [];
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const cur = new Date(start);
      cur.setDate(start.getDate() + w * 7 + d);
      const iso = `${cur.getFullYear()}-${pad2(cur.getMonth() + 1)}-${pad2(cur.getDate())}`;
      week.push({ iso, day: cur.getDate(), inMonth: cur.getMonth() + 1 === month });
    }
    grid.push(week);
  }
  return grid;
}

/**
 * Inner calendar component — reused by both the Dialog and the full
 * /cronograma page.  `open` is only relevant to the dialog wrapper;
 * when embedded directly, pass open={true} so it always loads.
 */
export function AgendaCalendar({ initialDate, onItemChanged }) {
  const base = (initialDate || todayIso()).slice(0, 10);
  const [year, setYear]   = useState(Number(base.slice(0, 4)));
  const [month, setMonth] = useState(Number(base.slice(5, 7))); // 1-12
  const [selected, setSelected] = useState(base);
  const [byDay, setByDay] = useState({});       // "YYYY-MM-DD" → { total, by_status }
  const [items, setItems] = useState([]);       // full items for current month
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState(null);   // item being edited
  const [createOpen, setCreateOpen] = useState(false);

  const monthKey = `${year}-${pad2(month)}`;
  const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/agenda/month", { params: { month: monthKey } });
      setByDay(r.data?.by_day || {});
      setItems(r.data?.items || []);
    } catch {
      setByDay({});
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [monthKey]);

  useEffect(() => { load(); }, [load]);

  // When moving between months make sure selection stays visible — if
  // the previously selected date is outside the current grid, pick the
  // first day of the current month.
  useEffect(() => {
    if (!selected.startsWith(monthKey)) setSelected(`${monthKey}-01`);
  }, [monthKey, selected]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const dayItems = useMemo(
    () => items.filter((it) => (it.date || "").slice(0, 10) === selected),
    [items, selected],
  );

  const today = todayIso();

  const handleItemSaved = () => {
    load();
    onItemChanged?.();
  };

  return (
    <div className="space-y-4">
      {/* Calendar card */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-3" data-testid="agenda-calendar">
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={prevMonth}
            className="p-1 rounded-full hover:bg-slate-100 text-slate-600"
            aria-label="Mês anterior"
            data-testid="agenda-cal-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h3 className="font-heading text-sm font-black text-slate-800 capitalize">
            {MONTH_NAMES[month - 1]} <span className="text-slate-400 font-semibold">{year}</span>
          </h3>
          <button
            type="button"
            onClick={nextMonth}
            className="p-1 rounded-full hover:bg-slate-100 text-slate-600"
            aria-label="Próximo mês"
            data-testid="agenda-cal-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-y-0.5 text-center">
          {DAY_SHORT.map((d, i) => (
            <div key={i} className="text-[10px] font-black uppercase text-slate-400 pb-0.5">{d}</div>
          ))}
          {grid.flat().map(({ iso, day, inMonth }) => {
            const isToday = iso === today;
            const isSelected = iso === selected;
            const summary = byDay[iso];
            const hasEvents = !!summary;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => setSelected(iso)}
                data-testid={`agenda-day-${iso}`}
                className={`relative h-8 w-full flex items-center justify-center rounded-full text-[12px] transition-colors ${
                  !inMonth ? "text-slate-300" :
                  isSelected ? "bg-[#7c3aed] text-white font-black" :
                  isToday    ? "bg-slate-900 text-white font-black" :
                  "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {day}
                {hasEvents && !isSelected && !isToday && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex items-center gap-0.5">
                    <span className="w-1 h-1 rounded-full bg-[#7c3aed]" />
                    {summary.total > 1 && <span className="w-1 h-1 rounded-full bg-[#7c3aed]" />}
                  </span>
                )}
                {hasEvents && (isSelected || isToday) && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Items for the selected day */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <CalendarDays className="w-4 h-4 text-[#7c3aed]" />
            <h4 className="font-heading text-sm font-black text-slate-800">
              {new Date(selected + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
            </h4>
          </div>
          <Button
            type="button"
            size="sm"
            className="h-7 px-2.5 text-[11px] bg-[#7c3aed] hover:bg-[#6d28d9] text-white"
            onClick={() => setCreateOpen(true)}
            data-testid="agenda-day-add"
          >
            <Plus className="w-3 h-3 mr-1" /> Novo
          </Button>
        </div>

        {loading && !items.length ? (
          <p className="text-xs text-slate-400 italic">Carregando...</p>
        ) : dayItems.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Nenhum evento. Aproveite o dia!</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {dayItems.map((it) => {
              const st = STATUS_META[it.status || "afio"] || STATUS_META.afio;
              const StIcon = st.Icon;
              return (
                <li
                  key={it.id}
                  onClick={() => setEditTarget(it)}
                  className="py-2.5 cursor-pointer hover:bg-slate-50 rounded-lg px-2 flex items-start gap-3"
                  data-testid={`agenda-day-item-${it.id}`}
                >
                  <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${st.tint} shrink-0`}>
                    <StIcon className="w-3.5 h-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-black text-slate-900 leading-tight truncate">
                      {it.title || "Sem título"}
                    </p>
                    <p className="text-[11px] text-slate-500 leading-tight truncate">
                      {it.time ? `${it.time} • ` : ""}{it.school_name || it.type || ""}
                    </p>
                  </div>
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${st.tint}`}>
                    {st.label}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Create / edit dialogs */}
      <AgendaItemFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultDate={selected}
        onSaved={() => { setCreateOpen(false); handleItemSaved(); }}
      />
      <AgendaItemFormDialog
        open={!!editTarget}
        onOpenChange={(o) => { if (!o) setEditTarget(null); }}
        editing={editTarget || undefined}
        onSaved={() => { setEditTarget(null); handleItemSaved(); }}
      />
    </div>
  );
}

export default function AgendaCalendarDialog({ open, onOpenChange, initialDate, onChanged }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="font-heading font-black text-[#1e3a8a]">Cronograma</DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-4 max-h-[80vh] overflow-y-auto">
          <AgendaCalendar initialDate={initialDate} onItemChanged={onChanged} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
